import { openDatabase, request, toBuffer, transactionDone, UPDATES_STORE } from './idb.js';
import type { UpdateLogStore } from './update-log.js';

/**
 * IndexedDB update log for the web.
 *
 * Written against the shared UpdateLogStore rather than pulling in y-indexeddb, so the web
 * and mobile adapters share one implementation, one compaction policy, and one set of
 * tests — and so nothing outside packages/core/src/yjs has to import Yjs.
 *
 * All documents live in one object store keyed by `[documentId, sequence]`, which keeps
 * per-document reads to a single range scan without a database per household.
 */
export class IndexedDbUpdateLog implements UpdateLogStore {
  private db: IDBDatabase | null = null;
  private sequence = 0;

  constructor(private readonly documentId: string) {}

  async open(): Promise<void> {
    this.db = await openDatabase();
  }

  private requireDb(): IDBDatabase {
    if (this.db == null) throw new Error('IndexedDbUpdateLog is not open');
    return this.db;
  }

  private range(): IDBKeyRange {
    return IDBKeyRange.bound([this.documentId, 0], [this.documentId, Number.MAX_SAFE_INTEGER]);
  }

  async readAll(): Promise<Uint8Array[]> {
    const tx = this.requireDb().transaction(UPDATES_STORE, 'readonly');
    const values = await request(tx.objectStore(UPDATES_STORE).getAll(this.range()));
    const keys = (await request(tx.objectStore(UPDATES_STORE).getAllKeys(this.range()))) as [
      string,
      number,
    ][];
    await transactionDone(tx);

    this.sequence = keys.reduce((max, key) => Math.max(max, key[1]), 0);
    return (values as ArrayBuffer[]).map((buffer) => new Uint8Array(buffer));
  }

  async append(update: Uint8Array): Promise<void> {
    this.sequence += 1;
    const tx = this.requireDb().transaction(UPDATES_STORE, 'readwrite');
    tx.objectStore(UPDATES_STORE).put(toBuffer(update), [this.documentId, this.sequence]);
    await transactionDone(tx);
  }

  async replaceAll(compacted: Uint8Array): Promise<void> {
    // Delete and rewrite in one transaction: a crash mid-compaction must leave either the
    // old log or the new snapshot, never an empty store.
    const tx = this.requireDb().transaction(UPDATES_STORE, 'readwrite');
    const store = tx.objectStore(UPDATES_STORE);
    store.delete(this.range());
    this.sequence = 1;
    store.put(toBuffer(compacted), [this.documentId, this.sequence]);
    await transactionDone(tx);
  }

  async count(): Promise<number> {
    const tx = this.requireDb().transaction(UPDATES_STORE, 'readonly');
    const total = await request(tx.objectStore(UPDATES_STORE).count(this.range()));
    await transactionDone(tx);
    return total;
  }

  async close(): Promise<void> {
    // The database connection is shared, so closing one log must not close it for the rest.
    this.db = null;
  }
}
